/**
 * 基盤スキーマ — 他のドメインスキーマから参照される共通定義
 */
import { z } from 'zod';
// ========================================
// 基盤 enum
// ========================================
/**
 * AI プロバイダー種別（Amazon Bedrock のみ）
 *
 * 旧プロバイダー（openai / anthropic / google）のサポート廃止前に保存されたタスク・
 * ワークスペース設定などを読めるよう、Bedrock 以外の値は 'bedrock' へ寄せる。
 * モデルIDは解決時（resolveModelId）に Bedrock の既定へフォールバックする。
 */
export const AIProviderTypeSchema = z.literal('bedrock').catch('bedrock');
/**
 * Reasoning モード（旧形式。互換用に残す。新しい指定は ReasoningLevelSchema を使う）
 */
export const ReasoningModeSchema = z.enum(['on', 'off']);
/**
 * 推論レベル（エフォート）。浅い順に並べる（丸めはこの順序の距離で行う）
 */
export const REASONING_LEVELS = ['off', 'low', 'medium', 'high', 'xhigh', 'max'];
/**
 * 推論レベル
 */
export const ReasoningLevelSchema = z.enum(REASONING_LEVELS);
/**
 * タイトル生成エラーコード
 */
export const TitleErrorCodeSchema = z.enum([
    'VALIDATION_ERROR',
    'PROVIDER_NOT_AVAILABLE',
    'GENERATION_FAILED',
]);
// ========================================
// 共通 enum
// ========================================
/**
 * タスクステータス
 */
export const TaskStatusSchema = z.enum(['running', 'completed', 'error', 'interrupted']);
// ========================================
// 値オブジェクト
// ========================================
/**
 * トークン使用量
 */
export const TokenUsageSchema = z.object({
    input: z.number(),
    output: z.number(),
    reasoning: z.number().optional(),
    cacheRead: z.number().optional(),
    cacheWrite: z.number().optional(),
});
/**
 * コスト情報
 */
export const CostsSchema = z.object({
    total: z.number(),
    input: z.number().optional(),
    output: z.number().optional(),
    reasoning: z.number().optional(),
    cacheRead: z.number().optional(),
    cacheWrite: z.number().optional(),
});
/**
 * LLM 使用量
 */
export const UsageSchema = z.object({
    tokens: TokenUsageSchema,
    costs: CostsSchema.optional(),
});
// ========================================
// メッセージ
// ========================================
/**
 * メッセージパート
 */
export const MessagePartSchema = z
    .object({
    type: z.string(),
})
    .passthrough();
// ========================================
// モデル関連
// ========================================
/**
 * モデル情報
 */
export const ModelInfoSchema = z.object({
    provider: z.string(),
    modelId: z.string(),
    displayName: z.string().optional(),
    /** その応答に実際に適用されたエフォート（モデルが受け付ける段階へ丸めた後）。未記録の過去の応答では無い */
    reasoningLevel: ReasoningLevelSchema.optional(),
});
/**
 * モデル選択（リクエストで使用）
 */
export const ModelSelectionSchema = z.object({
    provider: AIProviderTypeSchema,
    modelId: z.string(),
});
/**
 * 長文脈料金（入力トークン数が閾値を超えるリクエスト全体に適用する単価）
 */
export const LongContextPricingSchema = z.object({
    thresholdInputTokens: z.number().int().positive(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cachedTokens: z.number(),
    cacheWriteTokens: z.number(),
    reasoningTokens: z.number(),
});
/**
 * トークン単価情報
 */
export const ModelPricingSchema = z.object({
    inputTokens: z.number(),
    outputTokens: z.number(),
    cachedTokens: z.number().optional(),
    cacheWriteTokens: z.number().optional(),
    reasoningTokens: z.number().optional(),
    longContext: LongContextPricingSchema.optional(),
});
/**
 * 推論強度（Converse の追加リクエスト項目で指定する段階値）
 */
export const ReasoningEffortSchema = z.enum(['none', 'low', 'medium', 'high', 'xhigh', 'max']);
/**
 * Converse の additionalModelRequestFields で推論強度を書くフィールド名。
 * reasoning_config は Bedrock 側で reasoning.effort に変換される。
 * reasoning_effort は GPT-6 系で unknown_parameter として拒否され、Grok 4.7 / Kimi K3 では
 * エラーにならず黙って無視されるため選択肢に含めない（2026-10 実機確認）。
 */
export const ReasoningEffortFieldSchema = z.enum(['reasoning_config']);
/**
 * モデルが受け付ける推論レベルの集合（空でなく、浅い順・重複なし）
 */
const SupportedReasoningLevelsSchema = z
    .array(ReasoningLevelSchema)
    .min(1)
    .refine((levels) => levels.every((level, i) => i === 0 || REASONING_LEVELS.indexOf(levels[i - 1]) < REASONING_LEVELS.indexOf(level)), { message: '受け付ける推論レベルは浅い順・重複なしで並べてください' });
/**
 * 推論の指定方式
 * - none: 推論パラメータを送らない（推論非対応、またはモデル既定に任せる）
 * - adaptive: アダプティブ思考（reasoningConfig: adaptive + effort）。levels に off があれば
 *   offThinkingType の thinking.type（Sonnet 5.5 は between_tools、Haiku 5.5 は disabled）で推論を止められる
 * - effort: 推論強度の段階指定（additionalModelRequestFields の effortField に書く）。
 *   levels に off があれば none を送って推論を止められる
 */
export const ReasoningControlSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('none') }),
    z
        .object({
        kind: z.literal('adaptive'),
        levels: SupportedReasoningLevelsSchema,
        /** オフのときに送る thinking.type（levels に off を含むモデルだけが持ち、含むなら必須） */
        offThinkingType: z.enum(['between_tools', 'disabled']).optional(),
    })
        .refine((r) => r.levels.includes('off') === (r.offThinkingType !== undefined), {
        message: 'levels に off を含むときだけ offThinkingType を指定してください',
    }),
    z.object({
        kind: z.literal('effort'),
        effortField: ReasoningEffortFieldSchema,
        levels: SupportedReasoningLevelsSchema,
    }),
]);
/**
 * Bedrock モデルの能力情報（チャット Lambda が送信内容を決めるために参照する）
 */
export const BedrockModelCapabilitiesSchema = z.object({
    reasoning: ReasoningControlSchema,
    /** Converse の cachePoint による明示キャッシュに対応するか */
    supportsExplicitPromptCache: z.boolean(),
    /** モデルが受け付ける最大出力トークン */
    maxOutputTokens: z.number().int().positive(),
    /** PDF などの文書ブロック入力に対応するか */
    supportsDocumentInput: z.boolean(),
    /** 会話履歴に過去ターンの推論内容を含めてよいか */
    acceptsPriorReasoning: z.boolean(),
    /**
     * ツール結果の中に画像を含めてよいか。
     * false のモデルは Converse で「doesn't support the image field for user messages」を返すため、
     * 画像をツール結果の外（後続のユーザーメッセージ）へ移して送る
     */
    supportsToolResultImage: z.boolean(),
});
/**
 * モデル定義
 */
export const ModelDefinitionSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    provider: AIProviderTypeSchema,
    supportsReasoning: z.boolean(),
    supportsTools: z.boolean(),
    supportsVision: z.boolean().optional(),
    contextWindow: z.number(),
    pricing: ModelPricingSchema,
    region: z.string().optional(),
    /** Bedrock モデルの能力情報 */
    capabilities: BedrockModelCapabilitiesSchema.optional(),
});
/**
 * プロバイダー情報（モデル一覧を含む）
 */
export const ProviderInfoSchema = z.object({
    type: AIProviderTypeSchema,
    available: z.boolean(),
    models: z.array(ModelDefinitionSchema),
});
// ========================================
// 汎用レスポンス
// ========================================
/**
 * 汎用成功レスポンス（PUT/DELETE の { success: true }）
 */
export const SimpleSuccessResponseSchema = z.object({ success: z.boolean() });
//# sourceMappingURL=base-schemas.js.map